import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

function downloadDetailsFile(path, filename) {
  const link = document.createElement('a');
  link.href = path;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Chunked upload — root-caused via a paired client+server packet capture on
// a live repro: the server (nginx/Gunicorn) receives and ACKs 100% of what
// the client sends, zero drops on our side; the client's own TCP stack
// never receives a chunk of the SERVER's ACKs on the return path — packet
// loss between the droplet and the client's network, outside our
// infrastructure and not fixable via nginx/server tuning. Small,
// independently-retried chunks make the upload resilient to that class of
// loss instead of one large POST that fails outright.
//
// A prior chunked attempt sent chunk_index/upload_id as custom request
// headers (X-Chunk-Index, X-Upload-ID) — on a cross-origin multipart POST,
// custom headers force a CORS preflight (OPTIONS) that never reached
// nginx, so the chunk requests silently vanished. Every chunking value
// below travels as an ordinary multipart form field instead (see
// formData.append calls in handleUpload) — no custom headers are sent on
// chunk requests, only the same Authorization header every other
// authenticated endpoint on this app already requires.
// 128KB (was 512KB, 2026-10-02): on clients' lossy links nginx logged 408s after 300s with only 100-250KB of a
// 512KB chunk received (staging upload_diag.log, 2026-10-01) — smaller chunks each finish well inside the
// timeout and a lost one is cheaper to retry. 15MB max upload = 120 chunks.
const UPLOAD_CHUNK_SIZE = 128 * 1024;
const UPLOAD_CHUNK_TIMEOUT_MS = 30000;
// The edited-Excel route's FINAL chunk response is not a cheap byte-write
// ack like every other chunk — it's the request that synchronously runs
// the whole server-side pipeline (load_workbook + row classification +
// PDF render) and only then responds. That can easily exceed the ordinary
// 30s chunk timeout for a real client portfolio, which was sized for a
// plain disk write. When it does, the client aborts, isTransientChunkError
// treats the abort as retryable, and the retry redoes the same slow
// final-chunk work and aborts again — all 3 attempts failing identically
// with "Upload failed at part N of N after 3 attempts" and nothing after
// the FIRST chunk ever needing more than the ordinary timeout. Only the
// final chunk gets this longer allowance; every earlier chunk of the same
// upload is a fast byte-write like the original route's and keeps the
// normal timeout.
const UPLOAD_CHUNK_TIMEOUT_MS_FINAL_EDITED = 120000;
// Same for the statement upload's FINAL chunk: it synchronously parses the
// whole statement and matches every holding before responding (a 27-page
// CAS took >30s on staging on 2026-09-30, failing all 3 attempts). One
// allowance for both final-chunk routes, defined above.
const UPLOAD_CHUNK_TIMEOUT_MS_FINAL = UPLOAD_CHUNK_TIMEOUT_MS_FINAL_EDITED;
const UPLOAD_CHUNK_MAX_ATTEMPTS = 3;
// Backoff delay before attempt 2 and attempt 3 respectively (indexed by
// attempt - 2). No resume-on-reload: a failed/interrupted upload always
// restarts from chunk 1 with a fresh upload_id on the next attempt.
const UPLOAD_CHUNK_RETRY_DELAYS_MS = [2000, 5000];

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// crypto.randomUUID() requires a secure context (HTTPS or localhost) per
// spec — staging is served over plain HTTP, where it's undefined entirely,
// not just unpolyfilled (confirmed via a real browser console error:
// "crypto.randomUUID is not a function", upload never starts). This
// upload_id is only a client-generated correlation id for server-side
// chunk assembly, never used for anything security-sensitive, so it only
// needs to be unlikely to collide within one upload session on one tab —
// not cryptographically random. crypto.getRandomValues() (unlike
// randomUUID()) is NOT secure-context-restricted, so it's tried first;
// Math.random() is the last-resort fallback for a context missing even that.
const generateUploadId = () => {
  const bytes = new Uint8Array(16);
  if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// True only for a failure the chunk can plausibly succeed on retry — a
// timeout/abort or a request that never got a response. A definitive
// server rejection (e.g. bad file extension, file too large, integrity
// check failed) has a response and retrying it would just fail the same
// way three times.
const isTransientChunkError = (e) =>
  !e.response || e.code === 'ECONNABORTED' || e.code === 'ERR_CANCELED' ||
  e.name === 'CanceledError' || axios.isCancel(e);

export default function MFDPortfolioAudit() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // 'upload' | 'review'
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total } | null
  const [parseResult, setParseResult] = useState(null);
  const [msg, setMsg] = useState({ text: '', error: false });
  const [generating, setGenerating] = useState('');

  const [answers, setAnswers] = useState({ q1: '', q2: '' });
  const [clientName, setClientName] = useState('');

  // "Generate PDF from Edited Portfolio" -- a second, independent upload
  // flow on this same page/route: the MFD uploads the R06 Excel he
  // already edited and gets a PDF straight back, no Q1/Q2 step, no
  // intermediate review screen. Kept fully separate from the
  // file/step/parseResult/answers state above so the two flows can't
  // cross-contaminate each other's progress/messages.
  const editedFileInputRef = useRef(null);
  const editedCardRef = useRef(null);
  const [editedFile, setEditedFile] = useState(null);
  const [editedUploading, setEditedUploading] = useState(false);
  const [editedUploadProgress, setEditedUploadProgress] = useState(null);
  const [editedMsg, setEditedMsg] = useState({ text: '', error: false });
  // Set by "Upload Updated Excel" (review screen) to jump straight to the
  // second upload card instead of landing on the first, scrolled-to-top
  // default — consumed by the effect below once the upload step's cards
  // are back in the DOM. A plain ref, not state: it's never read in JSX,
  // only passed into the one effect below, and a state version of this
  // flag caused a real bug — clearing it via setState re-fired the effect
  // (it was a dependency of its own clearing call), and that second pass
  // saw pendingScrollToEdited already false and fell into the plain
  // window.scrollTo(0, 0) branch, snapping back to the top and stomping
  // the smooth scroll to the edited card that had just started a moment
  // earlier. A ref carries the same one-shot signal across renders
  // without ever triggering a re-render (or a second effect pass) on its
  // own, which is exactly the semantics this needs.
  const pendingScrollToEditedRef = useRef(false);

  const showMsg = (text, error = false) => setMsg({ text, error });
  const showEditedMsg = (text, error = false) => setEditedMsg({ text, error });

  // The upload screen must always open showing the FIRST upload card, not
  // wherever the page happened to be scrolled — both on first mount and
  // whenever we return to 'upload' from the review screen (start over,
  // etc). "Upload Updated Excel" is the one path that deliberately wants
  // the SECOND card instead, via pendingScrollToEdited.
  useEffect(() => {
    if (step !== 'upload') return;
    if (pendingScrollToEditedRef.current && editedCardRef.current) {
      editedCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      pendingScrollToEditedRef.current = false;
    } else {
      window.scrollTo(0, 0);
    }
  }, [step]);

  const goToUploadEditedExcel = () => {
    pendingScrollToEditedRef.current = true;
    setStep('upload');
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleUpload = async () => {
    if (!file) { showMsg('Please choose a file first.', true); return; }
    setUploading(true);
    setMsg({ text: '', error: false });
    const totalChunks = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_SIZE));
    setUploadProgress({ done: 0, total: totalChunks });
    // Fresh per upload attempt — no resume-on-reload. A failed or
    // interrupted upload, or a page reload mid-upload, always restarts
    // from chunk 1 with a brand new upload_id on the next attempt.
    const uploadId = generateUploadId();

    try {
      let finalRes = null;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * UPLOAD_CHUNK_SIZE;
        const chunk = file.slice(start, Math.min(file.size, start + UPLOAD_CHUNK_SIZE));

        // Chunk index/upload id/total chunks/total size travel as ordinary
        // multipart form fields, not headers — see the top-of-file note on
        // why that matters for CORS preflight.
        const formData = new FormData();
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', String(i));
        formData.append('total_chunks', String(totalChunks));
        formData.append('total_size', String(file.size));
        formData.append('filename', file.name);
        formData.append('chunk', chunk, file.name);

        const isFinalChunk = i === totalChunks - 1;
        let lastErr = null;
        let chunkRes = null;
        for (let attempt = 1; attempt <= UPLOAD_CHUNK_MAX_ATTEMPTS; attempt++) {
          if (attempt > 1) await sleep(UPLOAD_CHUNK_RETRY_DELAYS_MS[attempt - 2]);
          // axios' `timeout` option does not reliably abort a stalled
          // request-body upload in Chrome (it only covers a slow response).
          // An explicit AbortController is what actually cancels the send.
          const controller = new AbortController();
          const t = setTimeout(() => controller.abort(),
            isFinalChunk ? UPLOAD_CHUNK_TIMEOUT_MS_FINAL : UPLOAD_CHUNK_TIMEOUT_MS);
          try {
            chunkRes = await axios.post(`${API}/api/mfd/portfolio-audit/upload-chunk`, formData,
              { headers, signal: controller.signal });
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            if (!isTransientChunkError(e)) break; // definitive rejection — retrying won't help
          } finally {
            clearTimeout(t);
          }
        }

        if (lastErr) {
          if (!isTransientChunkError(lastErr)) {
            showMsg(lastErr.response?.data?.error || 'Upload failed.', true);
          } else {
            showMsg(`Upload failed at part ${i + 1} of ${totalChunks} after ${UPLOAD_CHUNK_MAX_ATTEMPTS} attempts. Please try uploading again.`, true);
          }
          return;
        }

        setUploadProgress({ done: i + 1, total: totalChunks });
        finalRes = chunkRes;
      }

      if (!finalRes.data || !finalRes.data.counts) {
        showMsg('Unexpected response from server. Please try again.', true);
        return;
      }
      setParseResult(finalRes.data);
      setAnswers({ q1: '', q2: '' });
      setStep('review');
    } catch (e) {
      showMsg(e.response?.data?.error || e.message || 'Upload failed.', true);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleEditedFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setEditedFile(f);
  };

  // Same chunk-assembly mechanics as handleUpload (same constants/helpers
  // above -- UPLOAD_CHUNK_SIZE, generateUploadId, isTransientChunkError,
  // sleep -- all already generic, nothing above them is upload-flow-
  // specific), pointed at the edited-Excel route instead. The one real
  // difference: every chunk response here is fetched with
  // responseType: 'blob' -- an intermediate chunk's small {received: i}
  // JSON body and the final chunk's PDF body both arrive as a Blob, so a
  // JSON body (an intermediate ack, or an error at any point) has to be
  // read back out via .text()/JSON.parse rather than axios' usual
  // automatic JSON parsing (which only applies to responseType: 'json',
  // the default) -- same technique handleGenerate's own catch block
  // already uses for a blob-wrapped error body.
  const handleEditedUpload = async () => {
    if (!editedFile) { showEditedMsg('Please choose a file first.', true); return; }
    setEditedUploading(true);
    setEditedMsg({ text: '', error: false });
    const totalChunks = Math.max(1, Math.ceil(editedFile.size / UPLOAD_CHUNK_SIZE));
    setEditedUploadProgress({ done: 0, total: totalChunks });
    const uploadId = generateUploadId();

    try {
      let finalRes = null;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * UPLOAD_CHUNK_SIZE;
        const chunk = editedFile.slice(start, Math.min(editedFile.size, start + UPLOAD_CHUNK_SIZE));

        const formData = new FormData();
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', String(i));
        formData.append('total_chunks', String(totalChunks));
        formData.append('total_size', String(editedFile.size));
        formData.append('filename', editedFile.name);
        formData.append('chunk', chunk, editedFile.name);

        const isFinalChunk = i === totalChunks - 1;
        let lastErr = null;
        let chunkRes = null;
        for (let attempt = 1; attempt <= UPLOAD_CHUNK_MAX_ATTEMPTS; attempt++) {
          if (attempt > 1) await sleep(UPLOAD_CHUNK_RETRY_DELAYS_MS[attempt - 2]);
          const controller = new AbortController();
          const t = setTimeout(() => controller.abort(),
            isFinalChunk ? UPLOAD_CHUNK_TIMEOUT_MS_FINAL_EDITED : UPLOAD_CHUNK_TIMEOUT_MS);
          try {
            chunkRes = await axios.post(`${API}/api/mfd/portfolio-audit/edited-excel/upload-chunk`, formData,
              { headers, signal: controller.signal, responseType: 'blob' });
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            if (!isTransientChunkError(e)) break;
          } finally {
            clearTimeout(t);
          }
        }

        if (lastErr) {
          if (isTransientChunkError(lastErr)) {
            showEditedMsg(`Upload failed at part ${i + 1} of ${totalChunks} after ${UPLOAD_CHUNK_MAX_ATTEMPTS} attempts. Please try uploading again.`, true);
          } else {
            let errText = 'Upload failed.';
            if (lastErr.response?.data) {
              try {
                const parsed = JSON.parse(await lastErr.response.data.text());
                errText = parsed.error || errText;
              } catch { /* not JSON */ }
            }
            showEditedMsg(errText, true);
          }
          return;
        }

        setEditedUploadProgress({ done: i + 1, total: totalChunks });
        finalRes = chunkRes;
      }

      const contentType = finalRes.headers['content-type'] || '';
      if (contentType.includes('application/pdf')) {
        const objUrl = window.URL.createObjectURL(new Blob([finalRes.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = objUrl;
        link.setAttribute('download', 'Portfolio_Audit_Final.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objUrl);
        showEditedMsg('PDF generated and downloaded.');
        setEditedFile(null);
        if (editedFileInputRef.current) editedFileInputRef.current.value = '';
      } else {
        let errText = 'Unexpected response from server. Please try again.';
        try {
          const parsed = JSON.parse(await finalRes.data.text());
          errText = parsed.error || errText;
        } catch { /* not JSON */ }
        showEditedMsg(errText, true);
      }
    } catch (e) {
      showEditedMsg(e.message || 'Upload failed.', true);
    } finally {
      setEditedUploading(false);
      setEditedUploadProgress(null);
    }
  };

  const setAnswer = (field, value) => setAnswers(prev => ({ ...prev, [field]: value }));

  const isComplete = () => !!(answers.q1 && answers.q2);

  const handleGenerate = async (format) => {
    if (!isComplete()) { showMsg('Please answer all required questions first.', true); return; }
    const key = format;
    setGenerating(key);
    setMsg({ text: '', error: false });
    try {
      const res = await axios.post(`${API}/api/mfd/portfolio-audit/generate`,
        { holdings: parseResult.holdings, answers, format, client_name: clientName },
        { headers, responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const objUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = objUrl;
      link.setAttribute('download', `Portfolio_Audit.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);
      showMsg('Portfolio Audit downloaded.');
    } catch (e) {
      let errText = 'Report generation failed.';
      if (e.response?.data) {
        try {
          const parsed = JSON.parse(await e.response.data.text());
          errText = parsed.error || errText;
        } catch { /* not JSON */ }
      }
      showMsg(errText, true);
    } finally {
      setGenerating('');
    }
  };

  const startOver = () => {
    setStep('upload');
    setFile(null);
    setParseResult(null);
    setAnswers({ q1: '', q2: '' });
    setClientName('');
    setMsg({ text: '', error: false });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div style={s.page}>
      <div style={s.navbar}>
        <div style={s.navLeft}>
          <button onClick={() => navigate('/mfd')} style={s.backBtn}>&#8592; Dashboard</button>
          <span style={s.brand}>Portfolio Audit</span>
        </div>
        <span style={s.reportBadge}>R06</span>
      </div>

      <div style={s.container}>
        {msg.text && (
          <div style={{ ...s.msgBox, ...(msg.error ? s.msgError : s.msgSuccess) }}>
            {msg.error ? 'Warning: ' : 'OK: '}{msg.text}
          </div>
        )}

        {step === 'upload' && (
          <div style={s.card}>
            <p style={s.pageDesc}>
              Upload a client's mutual fund holdings statement — a CAMS or KFintech Consolidated
              Account Statement (PDF), a Rumtek Current Portfolio report, or an Excel/CSV export.
              Each holding is matched against the FIP scored universe and scored where possible.
            </p>
            <div style={s.uploadZone} onClick={() => fileInputRef.current?.click()}>
              <div style={s.uploadIcon}>&#8593;</div>
              {file ? (
                <div style={s.uploadFileName}>{file.name}</div>
              ) : (
                <div style={s.uploadText}>Click to choose a PDF, XLSX, CSV or TXT file</div>
              )}
              <div style={s.uploadNote}>Max 15 MB</div>
            </div>
            <input ref={fileInputRef} type="file" accept=".pdf,.xlsx,.xls,.csv,.txt"
                   onChange={handleFileChange} style={{ display: 'none' }} />
            {uploading && uploadProgress && (
              <div style={s.progressWrap}>
                <div style={s.progressBarBg}>
                  <div style={{
                    ...s.progressBarFill,
                    width: `${uploadProgress ? Math.round((uploadProgress.done / uploadProgress.total) * 100) : 0}%`,
                  }} />
                </div>
                <div style={s.progressText}>
                  {uploadProgress && uploadProgress.done < uploadProgress.total
                    ? `Uploading… ${uploadProgress.done} of ${uploadProgress.total} parts`
                    : 'Processing statement…'}
                </div>
              </div>
            )}
            <div style={s.btnRow}>
              <button style={(!file || uploading) ? s.genBtnOff : s.genBtn}
                      disabled={!file || uploading} onClick={handleUpload}>
                {uploading
                  ? (uploadProgress && uploadProgress.done < uploadProgress.total ? 'Uploading...' : 'Parsing...')
                  : 'Upload & Parse'}
              </button>
            </div>
          </div>
        )}

        {step === 'upload' && (
          <div style={s.card} ref={editedCardRef}>
            <p style={s.editedPageDesc}>
              ALREADY EDITED A PORTFOLIO AUDIT EXCEL (PICKED REPLACEMENTS, RESOLVED MANUAL REVIEW
              FLAGS, FILLED IN NEW POSITION ROWS)? UPLOAD THAT FILE HERE TO GET A PDF REFLECTING
              YOUR FINAL PICKS — NO RE-MATCHING, NO QUESTIONS TO ANSWER AGAIN.
              <br />
              DO NOT UPLOAD ANY OTHER DOCUMENT — ONLY THE EXCEL FILE GENERATED BY FIP.
            </p>
            <div style={s.uploadZoneEdited} onClick={() => editedFileInputRef.current?.click()}>
              <div style={s.uploadIcon}>&#8593;</div>
              {editedFile ? (
                <div style={s.uploadFileName}>{editedFile.name}</div>
              ) : (
                <div style={s.uploadText}>Click to choose the edited Portfolio Audit .xlsx file</div>
              )}
              <div style={s.uploadNote}>Max 15 MB — .xlsx only</div>
            </div>
            <input ref={editedFileInputRef} type="file" accept=".xlsx"
                   onChange={handleEditedFileChange} style={{ display: 'none' }} />
            {editedMsg.text && (
              <div style={{ ...s.msgBox, marginTop: '16px', ...(editedMsg.error ? s.msgError : s.msgSuccess) }}>
                {editedMsg.error ? 'Warning: ' : 'OK: '}{editedMsg.text}
              </div>
            )}
            {editedUploading && editedUploadProgress && (
              <div style={s.progressWrap}>
                <div style={s.progressBarBg}>
                  <div style={{
                    ...s.progressBarFill,
                    width: `${editedUploadProgress ? Math.round((editedUploadProgress.done / editedUploadProgress.total) * 100) : 0}%`,
                  }} />
                </div>
                <div style={s.progressText}>
                  {editedUploadProgress && editedUploadProgress.done < editedUploadProgress.total
                    ? `Uploading… ${editedUploadProgress.done} of ${editedUploadProgress.total} parts`
                    : 'Generating PDF…'}
                </div>
              </div>
            )}
            <div style={s.btnRow}>
              <button style={(!editedFile || editedUploading) ? s.genBtnOff : s.pdfBtn}
                      disabled={!editedFile || editedUploading} onClick={handleEditedUpload}>
                {editedUploading
                  ? (editedUploadProgress && editedUploadProgress.done < editedUploadProgress.total ? 'Uploading...' : 'Generating...')
                  : 'Generate PDF from Edited Portfolio'}
              </button>
            </div>
          </div>
        )}

        {step === 'review' && parseResult && !parseResult.counts && (
          <div style={s.card}>
            <p style={s.pageDesc}>Something went wrong displaying the results. Please try uploading again.</p>
            <div style={s.btnRow}>
              <button style={s.genBtn} onClick={() => { setStep('upload'); setParseResult(null); }}>
                Start over
              </button>
            </div>
          </div>
        )}

        {step === 'review' && parseResult && parseResult.counts && (
          <>
            <div style={s.card}>
              <div style={s.summaryRow}>
                <span style={s.summaryBadge}>Format: {parseResult.format_detected || 'unknown'}</span>
                <span style={s.summaryBadge}>Matched: {parseResult.counts.matched}</span>
                <span style={s.summaryBadge}>Recognized (Not Scored): {parseResult.counts.recognized_not_scored}</span>
                <span style={s.summaryBadge}>Unidentified: {parseResult.counts.unidentified}</span>
                <span style={s.summaryBadge}>Current Value: {Number(parseResult.total_current_value).toLocaleString()}</span>
              </div>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Scheme</th><th style={s.th}>Status</th>
                    <th style={s.th}>Units</th><th style={s.th}>Current Value</th>
                  </tr>
                </thead>
                <tbody>
                  {parseResult.holdings.map((h, i) => (
                    <tr key={i}>
                      <td style={s.td}>
                        {(h.sheet_row && h.sheet_row.scheme_name) ||
                          (h.recognized_fund && h.recognized_fund.scheme_name) || h.scheme_name || '—'}
                        {h.unscored_reason && (
                          <div style={s.rowSubNote}>{h.unscored_reason}</div>
                        )}
                      </td>
                      <td style={s.td}>
                        {h.match_status === 'MATCHED' && <span style={s.matchedTag}>Matched</span>}
                        {h.match_status === 'RECOGNIZED_NOT_SCORED' && <span style={s.recognizedTag}>Recognized - Not Scored</span>}
                        {h.match_status === 'UNIDENTIFIED' && <span style={s.unmatchedTag}>Unidentified</span>}
                      </td>
                      <td style={s.td}>{h.units ?? '—'}</td>
                      <td style={s.td}>{h.current_value ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button style={s.linkBtn} onClick={startOver}>Start over with a different file</button>
            </div>

            <div style={s.card}>
              <p style={s.instrText}>{parseResult.instructional_text}</p>

              <div style={s.qBlock}>
                <label style={s.qLabel}>1. Client risk profile</label>
                <div style={s.optRow}>
                  {['Conservative', 'Moderate', 'Aggressive'].map(opt => (
                    <button key={opt} onClick={() => setAnswer('q1', opt)}
                            style={answers.q1 === opt ? s.optBtnActive : s.optBtn}>{opt}</button>
                  ))}
                </div>
              </div>

              <div style={s.qBlock}>
                <label style={s.qLabel}>2. What do you want out of this audit?</label>
                <div style={s.optCol}>
                  {(parseResult.q2_options || []).map(opt => (
                    <button key={opt} onClick={() => setAnswer('q2', opt)}
                            style={answers.q2 === opt ? s.optBtnActive : s.optBtn}>{opt}</button>
                  ))}
                </div>
              </div>

              <div style={s.qBlock}>
                <label style={s.qLabel}>Client Name</label>
                <input type="text" style={s.textInput} maxLength={80} value={clientName}
                       placeholder="Optional — appears on the report header"
                       onChange={(e) => setClientName(e.target.value)} />
              </div>

              <div style={{ ...s.msgBox, ...s.msgError, fontWeight: '700', marginBottom: '20px' }}>
                The Excel is a worksheet for manual fund replacements only — do not send it to the
                client. To generate the client-facing report, click "Start over with a different
                file" to return to the upload screen, then upload this Excel to the "Generate PDF
                from Edited Portfolio" section there.
              </div>

              <div style={s.btnRow}>
                <button style={(!isComplete() || generating === 'pdf') ? s.genBtnOff : s.pdfBtn}
                        disabled={!isComplete() || !!generating} onClick={() => handleGenerate('pdf')}>
                  {generating === 'pdf' ? 'Generating...' : 'Download PDF'}
                </button>
                <button style={(!isComplete() || generating === 'excel') ? s.genBtnOff : s.xlsBtn}
                        disabled={!isComplete() || !!generating} onClick={() => handleGenerate('excel')}>
                  {generating === 'excel' ? 'Generating...' : 'Download Excel'}
                </button>
                <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R06_Portfolio_Audit_Details.pdf', 'R06_Portfolio_Audit_Details.pdf')}>
                  Details
                </button>
                <button style={s.uploadUpdatedBtn} onClick={goToUploadEditedExcel}>
                  Upload Updated Excel
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page:        { minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Segoe UI', sans-serif" },
  navbar:      { background: '#1565C0', color: '#fff', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  navLeft:     { display: 'flex', alignItems: 'center', gap: '16px' },
  backBtn:     { background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  brand:       { fontWeight: '700', fontSize: '17px' },
  reportBadge: { fontSize: '11px', fontWeight: '700', color: '#1565C0', background: '#E3F0FF', padding: '3px 10px', borderRadius: '20px' },
  container:   { maxWidth: '900px', margin: '0 auto', padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: '20px' },
  pageDesc:    { fontSize: '14px', color: '#555', marginBottom: '20px', lineHeight: '1.6' },
  editedPageDesc: { fontSize: '14px', fontWeight: '700', color: '#CC0000', marginBottom: '20px', lineHeight: '1.6' },
  msgBox:      { borderRadius: '8px', padding: '12px 16px', fontSize: '14px' },
  msgSuccess:  { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
  msgError:    { background: '#ffebee', color: '#CC0000', border: '1px solid #ffcdd2' },
  card:        { background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
  uploadZone:  { border: '2px dashed #b3cff5', borderRadius: '10px', padding: '32px', textAlign: 'center', cursor: 'pointer', background: '#f8faff' },
  uploadZoneEdited: { border: '2px dashed #CC0000', borderRadius: '10px', padding: '32px', textAlign: 'center', cursor: 'pointer', background: '#f8faff' },
  uploadIcon:  { fontSize: '28px', color: '#1565C0', marginBottom: '8px' },
  uploadText:  { fontSize: '14px', color: '#555', marginBottom: '4px' },
  uploadFileName: { fontSize: '14px', color: '#1565C0', fontWeight: '600', marginBottom: '4px' },
  uploadNote:  { fontSize: '11px', color: '#aaa' },
  progressWrap:    { marginTop: '16px' },
  progressBarBg:   { width: '100%', height: '8px', borderRadius: '4px', background: '#e3eaf5', overflow: 'hidden' },
  progressBarFill: { height: '100%', background: '#1565C0', borderRadius: '4px', transition: 'width 0.2s ease' },
  progressText:    { fontSize: '12px', color: '#666', marginTop: '6px' },
  btnRow:      { display: 'flex', gap: '12px', marginTop: '20px' },
  genBtn:      { background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 28px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  genBtnOff:   { background: '#bdbdbd', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 28px', fontSize: '14px', fontWeight: '700', cursor: 'not-allowed' },
  pdfBtn:      { flex: 1, background: '#CC0000', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  xlsBtn:      { flex: 1, background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  detailsBtn:  { flex: 1, background: '#616161', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  uploadUpdatedBtn: { flex: 1, background: '#1565C0', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  summaryRow:  { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' },
  summaryBadge:{ background: '#E3F0FF', color: '#1565C0', fontSize: '12px', fontWeight: '700', padding: '5px 12px', borderRadius: '20px' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '14px' },
  th:          { textAlign: 'left', padding: '8px 10px', background: '#1565C0', color: '#fff', fontSize: '12px' },
  td:          { padding: '8px 10px', borderBottom: '1px solid #eee' },
  matchedTag:  { background: '#e8f5e9', color: '#2e7d32', fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' },
  recognizedTag:{ background: '#fff8e1', color: '#7D5A00', fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' },
  unmatchedTag:{ background: '#ffebee', color: '#CC0000', fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' },
  rowSubNote:  { fontSize: '11px', color: '#888', fontStyle: 'italic', marginTop: '3px' },
  linkBtn:     { background: 'none', border: 'none', color: '#1565C0', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline', padding: 0 },
  instrText:   { fontSize: '13px', color: '#666', background: '#f8faff', border: '1px solid #e0eaff', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', lineHeight: '1.6' },
  qBlock:      { marginBottom: '20px' },
  qLabel:      { display: 'block', fontSize: '13px', fontWeight: '700', color: '#333', marginBottom: '10px' },
  optRow:      { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  optCol:      { display: 'flex', flexDirection: 'column', gap: '8px' },
  optBtn:      { textAlign: 'left', padding: '9px 14px', fontSize: '13px', border: '1.5px solid #ddd', borderRadius: '8px', background: '#fff', color: '#555', cursor: 'pointer' },
  optBtnActive:{ textAlign: 'left', padding: '9px 14px', fontSize: '13px', border: '1.5px solid #1565C0', borderRadius: '8px', background: '#E3F0FF', color: '#1565C0', cursor: 'pointer', fontWeight: '700' },
  textInput:   { width: '100%', boxSizing: 'border-box', padding: '9px 14px', fontSize: '13px', border: '1.5px solid #ddd', borderRadius: '8px', color: '#333', fontFamily: "'Segoe UI', sans-serif" },
};
